import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import type { DebriefKind } from '../../../steps/investigation/DebriefFields'
import { ReadEntry, ReadItem, ReadWide } from '../../readLayout'
import { DEBRIEF_KEYS, type Debrief, namesOfIds } from '../../readRules'
import { formatDateOnly } from '../../readValues'
import { useTeamMemberName } from '../../usePersonName'

type DebriefReadSectionProps = {
  kind: DebriefKind
  debrief: Debrief
  testId: string
}

/**
 * One recorded debrief: what was learned, the cold debrief's improvement
 * actions, who attended and when it was completed.
 */
const DebriefReadSection = ({
  kind,
  debrief,
  testId
}: DebriefReadSectionProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const isCold = kind === 'coldDebrief'
  const sectionTestId = `${testId}-${isCold ? 'cold' : 'hot'}-debrief`
  const { hasImprovementActions, notesKey, titleKey } = DEBRIEF_KEYS[kind]
  const { isLoading, nameOf } = useTeamMemberName()
  const attendees = namesOfIds(debrief.debriefedUsers, nameOf)

  return (
    <ReadEntry title={t(titleKey)} testId={sectionTestId}>
      <ReadWide>
        <ReadItem
          label={t(notesKey)}
          value={debrief.notes}
          testId={`${sectionTestId}-notes`}
        />
      </ReadWide>
      {hasImprovementActions && (
        <ReadWide>
          <ReadItem
            label={t(
              incidentReportsConstants.CASE_V2_INVESTIGATION_IMPROVEMENT_ACTIONS
            )}
            value={debrief.preventativeActions}
            testId={`${sectionTestId}-improvement-actions`}
          />
        </ReadWide>
      )}
      <ReadItem
        isLoading={isLoading}
        label={t(incidentReportsConstants.CASE_V2_INVESTIGATION_ATTENDEES)}
        value={attendees}
        testId={`${sectionTestId}-attendees`}
      />
      <ReadItem
        label={t(incidentReportsConstants.CASE_V2_INVESTIGATION_COMPLETED_DATE)}
        value={formatDateOnly(debrief.conductedAt)}
        testId={`${sectionTestId}-completed-date`}
      />
    </ReadEntry>
  )
}

export default DebriefReadSection
